/**
 * CameraCheck - Camera verification step
 * Nordic Sanctuary design: Clear feedback, reassuring experience
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Camera, CameraOff, RefreshCw, CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "@jourfx/ui";

interface CameraCheckProps {
  onResult: (passed: boolean) => void;
  onContinue: () => void;
}

export function CameraCheck({ onResult, onContinue }: CameraCheckProps) {
  const { t } = useTranslation("techcheck");
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState<"checking" | "pass" | "fail">("checking");
  const [error, setError] = useState<string | null>(null);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const checkCamera = async () => {
    setStatus("checking");
    setError(null);

    // Stop any existing stream first
    stopStream();
    // Small delay to let the camera fully release
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480 },
      });

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      streamRef.current = mediaStream;
      setStatus("pass");
      onResult(true);
    } catch (err) {
      console.error("Camera check failed:", err);
      setStatus("fail");
      onResult(false);

      const domError = err as DOMException;
      if (domError.name === "NotAllowedError") {
        setError(t("checks.camera.permissionDenied"));
      } else if (domError.name === "NotFoundError") {
        setError(t("checks.camera.notFound"));
      } else if (domError.name === "AbortError") {
        setError(t("checks.camera.inUse"));
      } else {
        setError(t("checks.camera.genericError"));
      }
    }
  };

  useEffect(() => {
    checkCamera();
    return () => stopStream();
  }, []);

  const handleRetry = () => {
    checkCamera();
  };

  return (
    <div className="space-y-6">
      {/* Video preview */}
      <div className="relative aspect-video rounded-2xl overflow-hidden bg-[var(--stone-900)] shadow-lg">
        {status === "checking" && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-white/10 flex items-center justify-center">
                <Camera className="w-8 h-8 text-white animate-pulse" aria-hidden="true" />
              </div>
              <p className="text-white/80">{t("checks.camera.checking")}</p>
            </div>
          </div>
        )}

        {status === "fail" && (
          <div className="absolute inset-0 flex items-center justify-center p-6">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-[oklch(0.35_0.12_25)] flex items-center justify-center">
                <CameraOff className="w-8 h-8 text-white" aria-hidden="true" />
              </div>
              <p className="text-white/90 font-medium mb-1">{t("checks.camera.error")}</p>
              <p className="text-white/60 text-sm">{error}</p>
            </div>
          </div>
        )}

        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`w-full h-full object-cover ${status !== "pass" ? "hidden" : ""}`}
        />

        {status === "pass" && (
          <div className="absolute top-4 right-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[var(--sage-500)] text-white rounded-full text-sm font-medium shadow-lg">
              <CheckCircle className="w-4 h-4" aria-hidden="true" />
              {t("checks.camera.ok")}
            </div>
          </div>
        )}
      </div>

      {/* Status message */}
      {status === "pass" && (
        <div className="text-center">
          <p className="text-[var(--stone-600)]">
            {t("checks.camera.confirmMessage")}
          </p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        {status === "fail" && (
          <Button variant="secondary" size="client" className="flex-1" onClick={handleRetry}>
            <RefreshCw aria-hidden="true" />
            {t("actions.retry")}
          </Button>
        )}
        <Button
          variant={status === "pass" ? "default" : "secondary"}
          size="client"
          className="flex-1"
          onClick={onContinue}
          disabled={status === "checking"}
        >
          {status === "pass" ? (
            <>
              {t("actions.continue")}
              <ArrowRight aria-hidden="true" />
            </>
          ) : (
            t("actions.skip")
          )}
        </Button>
      </div>

      {status === "fail" && (
        <div className="rounded-xl border border-[var(--stone-200)] bg-[var(--stone-50)] p-4 space-y-2">
          <p className="text-sm font-medium text-[var(--stone-700)]">
            {t("checks.camera.skipWarning")}
          </p>
          <ul className="text-xs text-[var(--stone-600)] space-y-1">
            <li>{t("troubleshooting.camera")}</li>
          </ul>
        </div>
      )}
    </div>
  );
}
